import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy, RefreshCw, LogOut, Trash2, Globe, Lock, Pencil, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ClanLogo } from "../components/teams/ClanLogo";
import { LogoPicker } from "../components/teams/LogoPicker";
import { CreateClan, JoinClan } from "../components/teams/ClanOnboarding";
import { ChallengeCard } from "../components/teams/ChallengeCard";
import { MemberTable } from "../components/teams/MemberTable";
import { ClanLeaderboard } from "../components/teams/ClanLeaderboard";
import { BadgeList } from "../components/teams/Badges";

export default function Teams() {
  const { user } = useAuth();
  const [clan, setClan] = useState(undefined); // undefined = loading, null = none
  const [tab, setTab] = useState("create");

  useEffect(() => {
    if (user === null) return;
    if (user === false) { setClan(null); return; }
    api.get("/clans/mine").then((r) => setClan(r.data)).catch(() => setClan(null));
  }, [user]);

  if (user === false) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-10" data-testid="teams-guest">
        <div className="text-[11px] tracking-[0.3em] text-muted-foreground mb-2">// TEAMS</div>
        <h1 className="text-4xl font-extrabold tracking-tight mb-4">Clans need a <span className="text-phosphor">saved identity</span>.</h1>
        <p className="text-sm text-muted-foreground max-w-xl mb-6">Sign in to found a clan, join with an invite code, and have your drill scores count toward clan rankings.</p>
        <Link to="/auth" data-testid="teams-signin" className="bg-primary text-primary-foreground px-6 py-3 text-xs tracking-[0.25em] uppercase font-semibold hover:bg-primary/90">Sign in</Link>
      </div>
    );
  }

  if (clan === undefined) return <div className="max-w-5xl mx-auto px-6 py-10 text-muted-foreground">Loading clan…</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-xs tracking-widest text-muted-foreground hover:text-foreground mb-6" data-testid="back-dashboard">
        <ArrowLeft className="w-3 h-3" /> BACK
      </Link>

      {clan ? (
        <ClanHome clan={clan} meId={user.id} onUpdate={setClan} onLeft={() => setClan(null)} />
      ) : (
        <div data-testid="teams-onboarding">
          <div className="mb-8">
            <div className="text-[11px] tracking-[0.3em] text-muted-foreground mb-2">// TEAMS</div>
            <h1 className="text-4xl font-extrabold tracking-tight">Found a clan or <span className="text-phosphor">join one</span>.</h1>
            <p className="mt-3 text-sm text-muted-foreground max-w-2xl">One clan per person, up to 25 members. The owner picks a challenge; everyone's best scores feed the clan ranking and clan-vs-clan board.</p>
          </div>
          <div className="flex border border-border w-max mb-6">
            <button onClick={() => setTab("create")} data-testid="tab-create" className={`px-5 py-2 text-xs tracking-widest ${tab === "create" ? "bg-primary/10 text-phosphor" : "text-muted-foreground"}`}>CREATE</button>
            <button onClick={() => setTab("join")} data-testid="tab-join" className={`px-5 py-2 text-xs tracking-widest border-l border-border ${tab === "join" ? "bg-primary/10 text-phosphor" : "text-muted-foreground"}`}>JOIN</button>
          </div>
          <div className="grid lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3">{tab === "create" ? <CreateClan onCreated={setClan} /> : <JoinClan onJoined={setClan} />}</div>
            <div className="lg:col-span-2"><ClanLeaderboard refreshKey={0} /></div>
          </div>
        </div>
      )}
    </div>
  );
}

function ClanHome({ clan, meId, onUpdate, onLeft }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: clan.name, description: clan.description, public: clan.public, logo: clan.logo });
  const [refreshKey, setRefreshKey] = useState(0);

  const update = (data) => { onUpdate(data); setRefreshKey((k) => k + 1); };

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(clan.invite_code); toast.success("Invite code copied"); } catch { toast.error("Clipboard unavailable"); }
  };

  const regenerate = async () => { const { data } = await api.post("/clans/mine/regenerate-code"); update(data); toast.success("New invite code generated"); };

  const saveEdit = async () => {
    try {
      const { data } = await api.patch("/clans/mine", form);
      update(data); setEditing(false); toast.success("Clan updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };

  const leave = async () => {
    if (!window.confirm(clan.member_count === 1 ? "You're the last member — leaving will delete the clan. Continue?" : "Leave this clan?")) return;
    try { await api.post("/clans/leave"); onLeft(); toast.success("You left the clan"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not leave"); }
  };

  const disband = async () => {
    if (!window.confirm("Disband the clan for everyone? This cannot be undone.")) return;
    await api.delete("/clans/mine"); onLeft(); toast.success("Clan disbanded");
  };

  return (
    <div data-testid="clan-home">
      <div className="panel p-6 mb-6">
        {editing ? (
          <div className="space-y-4" data-testid="clan-edit-form">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={32} data-testid="edit-clan-name"
              className="w-full bg-transparent border border-phosphor/30 focus:border-phosphor outline-none px-4 py-3 text-lg font-bold" />
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={240} rows={2} data-testid="edit-clan-desc"
              className="w-full bg-transparent border border-phosphor/30 focus:border-phosphor outline-none px-4 py-3 text-sm" />
            <LogoPicker logo={form.logo} onChange={(logo) => setForm({ ...form, logo })} />
            <div className="flex items-center gap-3">
              <button onClick={() => setForm({ ...form, public: !form.public })} data-testid="edit-clan-public"
                className={`inline-flex items-center gap-2 px-4 py-2 border text-[10px] tracking-widest ${form.public ? "border-phosphor text-phosphor" : "border-border"}`}>
                {form.public ? <Globe className="w-3 h-3" /> : <Lock className="w-3 h-3" />} {form.public ? "PUBLIC" : "INVITE ONLY"}
              </button>
              <button onClick={saveEdit} data-testid="edit-clan-save" className="bg-primary text-primary-foreground px-5 py-2 text-[10px] tracking-widest font-semibold"><Check className="w-3 h-3 inline mr-1" /> SAVE</button>
              <button onClick={() => setEditing(false)} data-testid="edit-clan-cancel" className="border border-border px-5 py-2 text-[10px] tracking-widest">CANCEL</button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <ClanLogo logo={clan.logo} size={88} />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] tracking-[0.3em] text-muted-foreground mb-1 flex items-center gap-2">
                // YOUR CLAN {clan.public ? <Globe className="w-3 h-3" /> : <Lock className="w-3 h-3" />} {clan.public ? "PUBLIC" : "INVITE ONLY"}
              </div>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="clan-name">{clan.name}</h1>
              <p className="text-sm text-muted-foreground mt-1" data-testid="clan-desc">{clan.description || "No description yet."}</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-[10px] tracking-widest text-muted-foreground">INVITE CODE</span>
                <code className="border border-phosphor/40 px-3 py-1 text-sm tracking-[0.3em] text-phosphor" data-testid="invite-code">{clan.invite_code}</code>
                <button onClick={copyCode} data-testid="copy-invite" className="p-1.5 border border-border hover:border-phosphor" title="Copy"><Copy className="w-3.5 h-3.5" /></button>
                {clan.is_owner && <button onClick={regenerate} data-testid="regen-invite" className="p-1.5 border border-border hover:border-phosphor" title="Regenerate"><RefreshCw className="w-3.5 h-3.5" /></button>}
              </div>
            </div>
            <div className="flex md:flex-col gap-2">
              <div className="text-right mr-2 md:mr-0 md:mb-2">
                <div className="text-[10px] tracking-widest text-muted-foreground">CLAN SCORE</div>
                <div className="text-3xl font-extrabold text-phosphor tabular-nums" data-testid="clan-score">{clan.score === null ? "—" : clan.score}</div>
              </div>
              {clan.is_owner && <button onClick={() => setEditing(true)} data-testid="edit-clan-btn" className="border border-border px-3 py-2 text-[10px] tracking-widest hover:border-phosphor inline-flex items-center gap-2"><Pencil className="w-3 h-3" /> EDIT</button>}
              {clan.is_owner && clan.member_count > 1 ? (
                <button onClick={disband} data-testid="disband-btn" className="border border-destructive/50 text-destructive px-3 py-2 text-[10px] tracking-widest hover:bg-destructive/10 inline-flex items-center gap-2"><Trash2 className="w-3 h-3" /> DISBAND</button>
              ) : (
                <button onClick={leave} data-testid="leave-btn" className="border border-destructive/50 text-destructive px-3 py-2 text-[10px] tracking-widest hover:bg-destructive/10 inline-flex items-center gap-2"><LogOut className="w-3 h-3" /> LEAVE</button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-6">
          <ChallengeCard clan={clan} onUpdate={update} />
          <MemberTable clan={clan} meId={meId} onUpdate={update} />
        </div>
        <div className="lg:col-span-2 space-y-6">
          <ClanLeaderboard refreshKey={refreshKey} />
          <BadgeList endpoint="/clans/mine/trophies" title="Trophy Case" testid="clan-trophies"
            emptyText="Win a challenge window against other clans to fill the case. Badges go to every member." />
        </div>
      </div>
    </div>
  );
}
